import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Avatar, Box, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DateFormats,
  formatDate,
  isSameTime,
  parseDate,
  TimeUnit,
} from 'src/utils/time';
import {
  checkIsSameAuthor,
  getCurrentMessageTimestamp,
  getNextMessageTimestamp,
  getPreviousMessageTimestamp,
  MessageOrChannelEntry,
} from 'src/utils/messagesHelpers';
import { getInitials } from 'src/utils/getInitials';
import { formatMessageDate } from '../ReportMessages/formatMessageDate';

interface Props {
  messagesList: MessageOrChannelEntry[];
}

export const DrawerMessagesList = ({ messagesList }: Props) => {
  const { t } = useTranslation();
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const colors = {
    isAuthor: {
      backgroundColor: color.surface.subtle.value,
      avatarColor: color.status.error.background.value,
    },
    sender: {
      backgroundColor: color.status.notification.background.value,
      avatarColor: color.accent.yellow.background.value,
    },
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesList.length]);

  return (
    <Box
      data-testid="case-messages-all"
      sx={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        px: '16px',
        py: '8px',
        '&::-webkit-scrollbar': { width: '2px' },
        '&::-webkit-scrollbar-thumb': {
          background: color.ui.divider.default.value,
        },
      }}
    >
      {messagesList.map((message, index) => {
        const current = getCurrentMessageTimestamp(index, messagesList);
        const previous = getPreviousMessageTimestamp(index, messagesList);
        const next = getNextMessageTimestamp(index, messagesList);
        const sameAuthor = checkIsSameAuthor(index, messagesList);
        const showDate = !isSameTime(current, previous, TimeUnit.days);
        const isSameMinute =
          isSameTime(current, next, TimeUnit.minutes) && sameAuthor;
        const deletedAt = message.type === 'message' ? message.deletedAt : null;
        const showAuthorTime = !isSameMinute || Boolean(deletedAt);
        const date = formatMessageDate(current);
        const isReporter = message.isReporter;

        const authorLabel =
          message.author === 'Anonymous'
            ? t('public.ChatContainer.anonymousLabel')
            : (message.author ?? '');

        const parsedTimestamp = parseDate(
          deletedAt ?? message.createdAt,
          DateFormats.ISO,
        );
        const timeStr = formatDate(
          parsedTimestamp,
          DateFormats.TIME_SHORT,
          false,
        );
        const dayStr = formatDate(
          parsedTimestamp,
          DateFormats.DAY_MONTH_YEAR,
          false,
        );

        const messageText = deletedAt
          ? t('public.ChatContainer.deletedMessageLabel')
          : message.value;

        return (
          <React.Fragment key={message.id}>
            {showDate && (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', py: '4px' }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: color.type.muted.value,
                    letterSpacing: '0.3px',
                  }}
                >
                  {date}
                </Typography>
              </Box>
            )}

            {message.type === 'notification' ? (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', py: '2px' }}
              >
                <Typography
                  sx={{ fontSize: '12px', color: color.type.muted.value }}
                >
                  {message.value}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isReporter ? 'flex-start' : 'flex-end',
                  mb: sameAuthor ? '0px' : '4px',
                }}
              >
                <Box
                  data-testid={isReporter ? 'received-message' : 'sent-message'}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: '8px',
                    maxWidth: '85%',
                    backgroundColor: isReporter
                      ? colors.sender.backgroundColor
                      : colors.isAuthor.backgroundColor,
                    border: `1px solid ${
                      isReporter
                        ? 'var(--semantic-color-status-notification-default, #1dd9f9)'
                        : color.ui.divider.default.value
                    }`,
                    borderRadius: '12px',
                    padding: '12px',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.4px',
                      backgroundColor: isReporter
                        ? colors.sender.avatarColor
                        : colors.isAuthor.avatarColor,
                      color: color.type.default.value,
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(message.author)}
                  </Avatar>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '8px',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.2px',
                          color: color.type.default.value,
                        }}
                      >
                        {isReporter ? authorLabel || 'Reporter' : authorLabel}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 400,
                          lineHeight: '16px',
                          letterSpacing: '0.3px',
                          color: color.type.muted.value,
                        }}
                      >
                        {timeStr}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0.2px',
                        color: color.type.default.value,
                        wordBreak: 'break-word',
                      }}
                    >
                      {messageText}
                    </Typography>
                  </Box>
                </Box>

                {showAuthorTime && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: color.type.muted.value,
                      letterSpacing: '0.3px',
                      mt: '4px',
                      textAlign: isReporter ? 'left' : 'right',
                    }}
                  >
                    {authorLabel} | {timeStr} | {dayStr}
                    {message.type === 'message' &&
                    message.translated &&
                    isReporter
                      ? ` | ${t('public.ChatContainer.translatedMessages', 'Translated')}`
                      : null}
                  </Typography>
                )}
              </Box>
            )}
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </Box>
  );
};
